import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { CheckCircle2, Phone, ArrowRight, Mail, Clock, ShieldCheck } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { measureOpenAI } from "@/lib/openaiPixel";


declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const ThankYouSearchSchema = z.object({
  source: z.enum(["audit", "contact"]).optional(),
});

export const Route = createFileRoute("/thank-you")({
  validateSearch: (search) => ThankYouSearchSchema.parse(search),
  head: () => ({
    meta: [
      { title: "Thank You | Colorado Web Impressions" },
      { name: "description", content: "Thanks for reaching out to Colorado Web Impressions. We'll respond within 24 hours from our Colorado Springs HQ to schedule your free strategy call." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Thank You | Colorado Web Impressions" },
      { property: "og:description", content: "Thanks for reaching out to Colorado Web Impressions. We'll respond within 24 hours from our Colorado Springs HQ to schedule your free strategy call." },
    ],
  }),
  component: ThankYouPage,
});

function ThankYouPage() {
  const { source } = Route.useSearch();
  const isAudit = source === "audit";

  // Fire Google Ads conversion + GA4 lead event + Meta Pixel conversion once on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.gtag === "function") {
      // Google Ads conversion — gclid auto-attributes via the gtag config
      // installed in __root.tsx; no extra param needed here.
      window.gtag("event", "conversion", {
        send_to: "AW-10954529873/qsbnCLLp7ZMcENG4w-co",
      });
      // GA4 generate_lead.
      window.gtag("event", "generate_lead", {
        source: source ?? "contact",
      });
    }
    // Meta Pixel — fire CompleteRegistration so Facebook/Instagram Ads can
    // optimize against the same conversion event the Google Ads pixel uses.
    window.fbq?.("track", "CompleteRegistration", {
      content_name: source === "audit" ? "Free Audit Request" : "Contact Form",
      status: "complete",
    });
    // MNTN conversion pixel (shaid 66855) — production hostnames only so
    // preview/sandbox loads don't attribute conversions in MNTN.
    const h = window.location.hostname;
    if (h === "coloradowebimpressions.com" || h === "www.coloradowebimpressions.com") {
      try {
        let tdr = "";
        try { tdr = top!.document.referrer !== "" ? encodeURIComponent(top!.document.referrer.substring(0, 2048)) : ""; }
        catch { tdr = document.referrer ? document.referrer.toString().substring(0, 2048) : ""; }
        let plh = "";
        try { plh = parent.location.href !== "" ? encodeURIComponent(parent.location.href.toString().substring(0, 2048)) : ""; }
        catch { plh = ""; }
        const cb = Math.floor(Math.random() * 1e17);
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = `https://dx.mountain.com/spx?conv=1&shaid=66855&tdr=${tdr}&plh=${plh}&cb=${cb}&shoid=&shoamt=&type=&shocur=&shopid=&shoq=&shoup=&shpil=`;
        document.head.appendChild(s);
      } catch {}
    }

    // OpenAI Ads — backup lead_created conversion event on the confirmation
    // page in case the SDK had not loaded when the form handler fired.
    try {
      if (!sessionStorage.getItem("cwi_openai_lead_fired")) {
        const eventId =
          sessionStorage.getItem("cwi_contact_session_id") ||
          crypto.randomUUID?.() ||
          Math.random().toString(36).slice(2) + Date.now().toString(36);
        measureOpenAI(
          "lead_created",
          { type: "customer_action", source: source ?? "contact" },
          { event_id: eventId, value: 1, currency: "USD" },
        );
        sessionStorage.setItem("cwi_openai_lead_fired", "1");
      }
    } catch {}
  }, [source]);

  return (
    <SiteLayout>
      {/* Conversion marker for Google Ads / GA4 — gtag conversion fires via useEffect above */}
      <div id="conversion-thank-you" data-conversion="lead_submitted" />

      <section className="bg-gradient-to-b from-primary/5 to-background py-20 md:py-28">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-12 w-12 text-primary" strokeWidth={1.5} />
          </div>

          {isAudit ? (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
                You're all set — your free audit is on the way
              </h1>
              <p className="mt-4 text-lg text-muted-foreground md:text-xl">
                <strong className="text-foreground">Check your email</strong> for your custom video
                walkthrough from Chris. It will arrive within the next{" "}
                <strong className="text-foreground">2–3 business days</strong>.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
                Thank you — we got your request
              </h1>
              <p className="mt-4 text-lg text-muted-foreground md:text-xl">
                Chris Heidlebaugh, owner of Colorado Web Impressions, will personally review your
                request and get back to you as soon as possible.
              </p>
            </>
          )}

          <div className="mt-10 flex justify-center">
            <a
              href="tel:+17194193935"
              className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm transition hover:border-primary hover:shadow-md"
             aria-label="Call (719) 419-3935">
              <Phone className="h-5 w-5 text-primary" />
              <span className="font-semibold">Call (719) 419-3935</span>
            </a>
          </div>

          {isAudit ? (
            <>
              <div className="mt-12 rounded-2xl border border-border bg-card p-6 text-left md:p-8">
                <h2 className="text-xl font-semibold text-foreground md:text-2xl">
                  What happens next
                </h2>
                <ul className="mt-5 space-y-4 text-muted-foreground">
                  <li className="flex gap-3">
                    <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <span>
                      <strong className="text-foreground">Watch your inbox.</strong> Chris will
                      email you a personal video walkthrough showing exactly what's wrong with your
                      website, Google rankings, Maps listing, and social media.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <span>
                      <strong className="text-foreground">2–3 business days.</strong> Each audit is
                      done by hand — no AI shortcuts, no generic PDFs. That's how long it takes to
                      do it right.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <span>
                      <strong className="text-foreground">No sales pressure. No spam. No
                      follow-ups.</strong> This is a free service, no strings attached. If you ever
                      want to work together, you'll reach out to us.
                    </span>
                  </li>
                </ul>
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                Don't see the email in a few days? Check your spam folder, or call{" "}
                <a href="tel:+17194193935" className="font-semibold text-primary hover:underline" aria-label="Call (719) 419-3935">
                  (719) 419-3935
                </a>
                .
              </p>
            </>
          ) : (
            <div className="mt-12 rounded-2xl border border-border bg-card p-6 text-left md:p-8">
              <h2 className="text-xl font-semibold text-foreground md:text-2xl">
                While you wait, here's what's next
              </h2>
              <ol className="mt-4 space-y-3 text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    1
                  </span>
                  <span>Chris will review your goals, current site, and project needs.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    2
                  </span>
                  <span>During business hours, expect a reply back within an hour.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    3
                  </span>
                  <span>If we're a fit, we'll get started on your project right away!</span>
                </li>
              </ol>
            </div>
          )}

          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              size="lg"
              className="bg-gradient-gold font-bold uppercase tracking-wider text-secondary shadow-elegant transition-all hover:opacity-90 hover:shadow-lg"
            >
              <Link to="/digital-marketing">
                View Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
